/**
 * UserPermissionReport.jsx
 *
 * Rapport actionnable des permissions manquantes par utilisateur, basé sur
 * les 403 réels enregistrés dans `rbac_denials`.
 *
 * L'admin peut :
 *   - Voir chaque utilisateur bloqué avec ses permissions manquantes
 *   - Voir les endpoints où il se fait refuser
 *   - Voir un pack recommandé (avec ratio de couverture)
 *   - Appliquer le correctif en 1 clic (union de permissions au rôle)
 *   - Exporter la liste en CSV
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  ShieldAlert, Download, RefreshCw, Zap, Info, User, ChevronRight,
  CheckCircle2, AlertCircle, Package, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const UserPermissionReport = () => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null); // user_id being fixed
  const [report, setReport] = useState(null);
  const [sinceDays, setSinceDays] = useState('7');
  const [minDenials, setMinDenials] = useState('1');
  const [detailUser, setDetailUser] = useState(null);
  const [rawLogs, setRawLogs] = useState(null); // {user_id, denials: [...]}
  const [loadingRaw, setLoadingRaw] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/rbac/monitoring/user-permission-report`,
        {
          params: { since_days: sinceDays, min_denials: minDenials },
          headers: authHeader(),
        }
      );
      setReport(data);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Impossible de charger le rapport',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [sinceDays, minDenials]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const downloadCsv = async () => {
    try {
      const response = await axios.get(
        `${API}/rbac/monitoring/user-permission-report`,
        {
          params: { since_days: sinceDays, min_denials: minDenials, format: 'csv' },
          headers: authHeader(),
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `user-permission-report-${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Export CSV téléchargé' });
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Export impossible',
        variant: 'destructive',
      });
    }
  };

  const openRawLogs = async (user) => {
    setLoadingRaw(true);
    setRawLogs({ user_id: user.user_id, user_email: user.user_email, user_name: user.user_name, denials: null });
    try {
      const { data } = await axios.get(
        `${API}/rbac/monitoring/user-denials/${user.user_id}`,
        { params: { since_days: sinceDays, limit: 200 }, headers: authHeader() }
      );
      setRawLogs(data);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Logs indisponibles',
        variant: 'destructive',
      });
      setRawLogs(null);
    } finally {
      setLoadingRaw(false);
    }
  };

  const applyQuickFix = async (user) => {
    const qf = user.quick_fix || {};
    const confirmMsg =
      qf.type === 'apply_pack'
        ? `Appliquer le pack "${qf.pack_name}" au rôle "${user.user_role_name}" ?\n\nCela ajoute des permissions (union), n'en retire jamais.`
        : `Ajouter directement ${qf.permissions_to_add?.length || 0} permission(s) au rôle "${user.user_role_name}" ?`;
    if (!window.confirm(confirmMsg)) return;

    setApplying(user.user_id);
    try {
      const payload =
        qf.type === 'apply_pack'
          ? { role_slug: user.user_role_slug, pack_slug: qf.pack_slug }
          : { role_slug: user.user_role_slug, permissions_to_add: qf.permissions_to_add };

      const { data } = await axios.post(
        `${API}/rbac/monitoring/apply-quick-fix`,
        payload,
        { headers: authHeader() }
      );
      toast({
        title: 'Correctif appliqué',
        description: `+${data.permissions_added_count} permission(s) sur "${data.role_name}"`,
      });
      // Refresh report
      await fetchReport();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err?.response?.data?.detail || 'Correctif impossible',
        variant: 'destructive',
      });
    } finally {
      setApplying(null);
    }
  };

  const users = report?.users || [];

  return (
    <div className="space-y-4" data-testid="user-permission-report">
      {/* Header + Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-orange-500" />
                Rapport des permissions utilisateur
              </CardTitle>
              <CardDescription className="mt-1">
                Basé sur les refus d&apos;accès (403) réellement enregistrés. Permet
                d&apos;identifier et corriger les rôles/packs manquants en 1 clic.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={sinceDays} onValueChange={setSinceDays}>
                <SelectTrigger className="w-[140px]" data-testid="rbac-report-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Dernières 24h</SelectItem>
                  <SelectItem value="7">7 derniers jours</SelectItem>
                  <SelectItem value="30">30 derniers jours</SelectItem>
                  <SelectItem value="90">90 derniers jours</SelectItem>
                </SelectContent>
              </Select>
              <Select value={minDenials} onValueChange={setMinDenials}>
                <SelectTrigger className="w-[140px]" data-testid="rbac-report-min-denials">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">≥ 1 refus</SelectItem>
                  <SelectItem value="3">≥ 3 refus</SelectItem>
                  <SelectItem value="5">≥ 5 refus</SelectItem>
                  <SelectItem value="10">≥ 10 refus</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={fetchReport}
                disabled={loading}
                data-testid="rbac-report-refresh"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button
                variant="outline"
                onClick={downloadCsv}
                disabled={loading || users.length === 0}
                data-testid="rbac-report-export-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* KPI summary */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Utilisateurs bloqués</p>
                  <p className="text-2xl font-bold" data-testid="rbac-report-users-count">
                    {report.total_users_with_denials}
                  </p>
                </div>
                <User className="w-8 h-8 text-orange-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total 403 sur la période</p>
                  <p className="text-2xl font-bold">
                    {users.reduce((s, u) => s + (u.total_denials || 0), 0)}
                  </p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Corrections en 1 clic dispo</p>
                  <p className="text-2xl font-bold">
                    {users.filter(u => u.quick_fix?.type === 'apply_pack').length}
                  </p>
                </div>
                <Zap className="w-8 h-8 text-emerald-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Chargement…</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 space-y-2">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <p className="text-lg font-medium">Aucun refus d&apos;accès sur la période</p>
              <p className="text-sm text-gray-500">
                Les permissions actuelles couvrent tous les usages détectés.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead className="text-right">Refus</TableHead>
                  <TableHead>Permissions manquantes</TableHead>
                  <TableHead>Pack recommandé</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const topPack = u.recommended_packs?.[0];
                  const isApplying = applying === u.user_id;
                  return (
                    <TableRow key={u.user_id} data-testid={`rbac-report-row-${u.user_id}`}>
                      <TableCell>
                        <div className="font-medium">{u.user_name}</div>
                        <div className="text-xs text-gray-500">{u.user_email}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          <Badge variant="outline" className="text-[10px]">
                            {u.user_type === 'internal' ? 'Staff' : 'Client'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{u.user_role_name}</Badge>
                        <div className="text-xs text-gray-500 mt-1">
                          {u.current_permissions_count} perm.
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-lg font-semibold text-red-600">
                          {u.total_denials}
                        </span>
                        <div className="text-xs text-gray-500">
                          {u.unique_missing_permissions} unique
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                          {u.missing_permissions.slice(0, 3).map((mp) => (
                            <Badge
                              key={mp.permission}
                              variant="destructive"
                              className="text-[10px] font-mono"
                            >
                              {mp.permission}
                              <span className="ml-1 opacity-70">×{mp.count}</span>
                            </Badge>
                          ))}
                          {u.missing_permissions.length > 3 && (
                            <Badge variant="outline" className="text-[10px]">
                              +{u.missing_permissions.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {topPack ? (
                          <div>
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4" style={{ color: topPack.pack_color }} />
                              <span className="font-medium text-sm">{topPack.pack_name}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Couvre {topPack.coverage_count}/{topPack.missing_total} (
                              {Math.round(topPack.coverage_ratio * 100)}%)
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">
                            Aucun pack — ajout manuel
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openRawLogs(u)}
                            title="Voir les logs bruts"
                            data-testid={`rbac-report-rawlogs-${u.user_id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDetailUser(u)}
                            data-testid={`rbac-report-detail-${u.user_id}`}
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => applyQuickFix(u)}
                            disabled={isApplying}
                            data-testid={`rbac-report-fix-${u.user_id}`}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            {isApplying ? '…' : 'Corriger'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!detailUser} onOpenChange={(v) => !v && setDetailUser(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {detailUser?.user_name}
            </DialogTitle>
            <DialogDescription>
              {detailUser?.user_email} — Rôle actuel :{' '}
              <Badge variant="secondary">{detailUser?.user_role_name}</Badge>
            </DialogDescription>
          </DialogHeader>

          {detailUser && (
            <div className="space-y-5">
              {/* Missing permissions */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  Permissions manquantes ({detailUser.unique_missing_permissions})
                </h4>
                <div className="space-y-1">
                  {detailUser.missing_permissions.map((mp) => (
                    <div
                      key={mp.permission}
                      className="flex justify-between text-sm border-b py-1"
                    >
                      <code className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded">
                        {mp.permission}
                      </code>
                      <span className="text-gray-600">×{mp.count} refus</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Blocked endpoints */}
              <div>
                <h4 className="font-semibold mb-2">Endpoints bloqués</h4>
                <div className="space-y-1">
                  {detailUser.blocked_endpoints.map((ep, i) => (
                    <div
                      key={`${ep.path}-${ep.method}-${i}`}
                      className="flex justify-between text-sm border-b py-1"
                    >
                      <span className="font-mono text-xs">
                        <Badge variant="outline" className="mr-2 text-[10px]">
                          {ep.method}
                        </Badge>
                        {ep.path}
                      </span>
                      <span className="text-gray-600">×{ep.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended packs */}
              {detailUser.recommended_packs?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4 text-emerald-500" />
                    Packs recommandés
                  </h4>
                  <div className="space-y-2">
                    {detailUser.recommended_packs.map((p) => (
                      <div
                        key={p.pack_slug}
                        className="border rounded p-3 hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 font-medium">
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: p.pack_color }}
                              />
                              {p.pack_name}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Couverture : {p.coverage_count}/{p.missing_total} (
                              {Math.round(p.coverage_ratio * 100)}%) — ajoute{' '}
                              {p.added_permissions_count} permission(s) au rôle
                            </div>
                          </div>
                          <Badge
                            variant={p.coverage_ratio >= 0.75 ? 'default' : 'outline'}
                          >
                            {Math.round(p.coverage_ratio * 100)}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick fix summary */}
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <div className="flex items-start gap-2">
                  <Zap className="w-5 h-5 text-emerald-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-emerald-800">
                      Correction proposée
                    </div>
                    <div className="text-sm text-emerald-700 mt-1">
                      {detailUser.quick_fix?.type === 'apply_pack' ? (
                        <>
                          Appliquer le pack{' '}
                          <strong>{detailUser.quick_fix.pack_name}</strong> au rôle{' '}
                          <strong>{detailUser.user_role_name}</strong>.
                        </>
                      ) : (
                        <>
                          Ajouter directement{' '}
                          <strong>
                            {detailUser.quick_fix?.permissions_to_add?.length || 0}
                          </strong>{' '}
                          permission(s) au rôle{' '}
                          <strong>{detailUser.user_role_name}</strong>.
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailUser(null)}>
              Fermer
            </Button>
            {detailUser && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    openRawLogs(detailUser);
                    setDetailUser(null);
                  }}
                  data-testid="rbac-report-detail-viewlogs"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Voir logs bruts
                </Button>
                <Button
                  onClick={() => {
                    applyQuickFix(detailUser);
                    setDetailUser(null);
                  }}
                  data-testid="rbac-report-detail-apply"
                >
                  <Zap className="w-4 h-4 mr-1" />
                  Appliquer le correctif
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Denials Log Viewer */}
      <Dialog open={!!rawLogs} onOpenChange={(v) => !v && setRawLogs(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Logs bruts — {rawLogs?.user_name || rawLogs?.user_email || rawLogs?.user_id}
            </DialogTitle>
            <DialogDescription>
              Chaque ligne = un refus 403 enregistré. Utile pour identifier les
              endpoints exacts et le contexte de chaque blocage.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {loadingRaw ? (
              <div className="text-center py-12 text-gray-500">Chargement des logs…</div>
            ) : !rawLogs?.denials ? (
              <div className="text-center py-12 text-gray-500">Aucune donnée</div>
            ) : rawLogs.denials.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p>Aucun refus enregistré pour cet utilisateur sur la période.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-gray-500 sticky top-0 bg-white py-2 border-b flex items-center justify-between">
                  <span>
                    <strong>{rawLogs.total || rawLogs.denials.length}</strong> refus sur les {sinceDays}{' '}
                    derniers jours
                  </span>
                  <Badge variant="outline">
                    {rawLogs.user_role || 'rôle inconnu'}
                  </Badge>
                </div>
                {rawLogs.denials.map((d) => (
                  <div
                    key={d.id}
                    className="border rounded-md p-3 hover:bg-gray-50 text-sm"
                    data-testid={`raw-denial-${d.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] font-mono">
                          {d.endpoint_method || '?'}
                        </Badge>
                        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">
                          {d.endpoint_path}
                        </code>
                        {d.query_string && (
                          <code className="text-[10px] text-gray-500 font-mono">
                            ?{d.query_string}
                          </code>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {new Date(d.timestamp).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 flex-wrap">
                      {(d.required_permissions || []).length > 0 ? (
                        d.required_permissions.map((p) => (
                          <Badge
                            key={p}
                            variant="destructive"
                            className="text-[10px] font-mono"
                          >
                            {p}
                          </Badge>
                        ))
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-gray-500">
                          (aucune perm extraite)
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px]">
                        mode: {d.check_mode || 'unknown'}
                      </Badge>
                    </div>
                    {d.detail && (
                      <div className="mt-2 text-xs text-gray-600 italic">
                        {d.detail}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRawLogs(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserPermissionReport;
